import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Invoices from "@/pages/Invoices";
import Quotes from "@/pages/Quotes";
import Expenses from "@/pages/Expenses";
import Suppliers from "@/pages/Suppliers";
import Disputes from "@/pages/Disputes";
import Clients from "@/pages/Clients";
import Recurrences from "@/pages/Recurrences";
import Contracts from "@/pages/Contracts";
import Emails from "@/pages/Emails";
import Reports from "@/pages/Reports";
import UsersPage from "@/pages/Users";
import Settings from "@/pages/Settings";
import Portal from "@/pages/Portal";
import SupplierPortal from "@/pages/SupplierPortal";
import SupplierRegister from "@/pages/SupplierRegister";
import ClientRegister from "@/pages/ClientRegister";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-muted-foreground text-sm tracking-widest uppercase">Chargement…</div>
    </div>
  );
}

function StaffRoute({ children, adminOnly = false }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "client") return <Navigate to="/portail" replace />;
  if (user.role === "supplier") return <Navigate to="/portail-fournisseur" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/tableau-de-bord" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function ClientRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "supplier") return <Navigate to="/portail-fournisseur" replace />;
  if (user.role !== "client") return <Navigate to="/tableau-de-bord" replace />;
  return children;
}

function SupplierRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "supplier") return <Navigate to="/" replace />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "client") return <Navigate to="/portail" replace />;
  if (user.role === "supplier") return <Navigate to="/portail-fournisseur" replace />;
  return <Navigate to="/tableau-de-bord" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/inscription-fournisseur" element={<SupplierRegister />} />
            <Route path="/inscription-client" element={<ClientRegister />} />
            <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
            <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
            <Route path="/tableau-de-bord" element={<StaffRoute><Dashboard /></StaffRoute>} />
            <Route path="/factures" element={<StaffRoute><Invoices /></StaffRoute>} />
            <Route path="/soumissions" element={<StaffRoute><Quotes /></StaffRoute>} />
            <Route path="/contrats" element={<StaffRoute><Contracts /></StaffRoute>} />
            <Route path="/courriels" element={<StaffRoute><Emails /></StaffRoute>} />
            <Route path="/depenses" element={<StaffRoute><Expenses /></StaffRoute>} />
            <Route path="/fournisseurs" element={<StaffRoute><Suppliers /></StaffRoute>} />
            <Route path="/ecarts" element={<StaffRoute><Disputes /></StaffRoute>} />
            <Route path="/clients" element={<StaffRoute><Clients /></StaffRoute>} />
            <Route path="/recurrences" element={<StaffRoute><Recurrences /></StaffRoute>} />
            <Route path="/rapports" element={<StaffRoute><Reports /></StaffRoute>} />
            <Route path="/utilisateurs" element={<StaffRoute adminOnly><UsersPage /></StaffRoute>} />
            <Route path="/parametres" element={<StaffRoute adminOnly><Settings /></StaffRoute>} />
            <Route path="/portail" element={<ClientRoute><Portal /></ClientRoute>} />
            <Route path="/portail-fournisseur" element={<SupplierRoute><SupplierPortal /></SupplierRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
